import {
  CRMChannel,
  EmailConversationModel,
  TwilioConversationModel,
  WebChatBotConversationModel,
} from "@jugl-web/rest-api";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { VoiceConversationModel } from "@jugl-web/rest-api/integrations/models/VoiceConversation";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { MultiSectionLayout } from "@jugl-web/ui-kit";
import { cx, useDebounce } from "@jugl-web/utils";
import { FC, useRef } from "react";
import { useIsInboxSinglePane } from "../../hooks/useIsInboxSinglePane";
import { useClientSupportPageContext } from "../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ConversationSidePanel } from "../ConversationSidePanel";
import { ChatTopBar } from "./components/ChatTopBar";
import { InstagramChatInput } from "./components/InstagramChatInput";
import {
  InstagramChatMessageWindow,
  InstagramChatMessageWindowHandle,
} from "./components/InstagramChatMessageWindow";
import { SelectChatSection } from "./components/SelectChatSection";
import { TwilioChatInput } from "./components/TwilioChatInput";
import {
  TwilioChatMessageWindow,
  TwilioChatMessageWindowHandle,
} from "./components/TwilioChatMessageWindow";
import { EmailChatInput } from "./components/EmailChatInput";
import {
  EmailChatMessageWindow,
  EmailChatMessageWindowHandle,
} from "./components/EmailChatMessageWindow";
import { WebChatInput } from "./components/WebChatInput";
import {
  WebChatMessageWindow,
  WebChatMessageWindowHandle,
} from "./components/WebChatMessageWindow";
import { VoiceChatMessageWindow } from "./components/VoiceChatMessageWindow";
import { WhatsappChatInput } from "./components/WhatsappChatInput/WhatsappChatInput";
import {
  WhatsappChatMessageWindow,
  WhatsappChatMessageWindowHandle,
} from "./components/WhatsappChatMessageWindow/WhatsappChatMessageWindow";

const SIDE_PANEL_TRANSITION_DURATION_MS = 200;

const ClientSupportChatInner: FC<{
  selectedConversation:
    | WhatsappConversationModel
    | InstagramConversationModel
    | WebChatBotConversationModel
    | TwilioConversationModel
    | VoiceConversationModel
    | EmailConversationModel;
}> = ({ selectedConversation }) => {
  const { selectedChannel, isContactBlocked, isConversationSidePanelOpen } =
    useClientSupportPageContext();

  const wasRecentlyOpen = Boolean(
    useDebounce(isConversationSidePanelOpen, SIDE_PANEL_TRANSITION_DURATION_MS)
  );

  const isWhatsappChannel = selectedChannel === CRMChannel.whatsapp;
  const isWebChannel = selectedChannel === CRMChannel.chatbot;
  const isInstagramChannel = selectedChannel === CRMChannel.instagram;
  const isTwilioChannel = selectedChannel === CRMChannel.twilio;
  const isVoiceChannel = selectedChannel === CRMChannel.voice;
  const isEmailChannel = selectedChannel === CRMChannel.email;

  const messageWindowRef = useRef<WhatsappChatMessageWindowHandle>(null);
  const instagramMessageWindowRef =
    useRef<InstagramChatMessageWindowHandle>(null);
  const webMessageWindowRef = useRef<WebChatMessageWindowHandle>(null);
  const twilioMessageWindowRef = useRef<TwilioChatMessageWindowHandle>(null);
  const emailMessageWindowRef = useRef<EmailChatMessageWindowHandle>(null);

  return (
    <>
      <MultiSectionLayout
        className="h-full w-full min-w-0 flex-1 max-lg:absolute max-lg:inset-0 max-lg:z-10 max-lg:bg-white"
        classNames={{
          middle:
            "w-0 min-w-[560px] flex-1 min-h-0 overflow-hidden max-lg:w-full max-lg:min-w-0",
          rightSide: cx(
            "h-full overflow-hidden transition-[flex-basis,min-width,max-width] duration-200 ease-linear",
            isConversationSidePanelOpen
              ? "flex-[0_1_420px] min-w-[360px] max-w-[420px] max-lg:absolute max-lg:inset-0 max-lg:z-20 max-lg:min-w-0 max-lg:max-w-none max-lg:transition-none"
              : "flex-[0_0_0px] min-w-0 max-w-0 max-lg:hidden"
          ),
          content: "min-h-0 overflow-x-hidden",
        }}
        header={<ChatTopBar />}
        rightSide={
          isConversationSidePanelOpen || wasRecentlyOpen ? (
            <ConversationSidePanel />
          ) : (
            <div />
          )
        }
        footer={
          !isContactBlocked && !isVoiceChannel ? (
            isWebChannel ? (
              <WebChatInput
                chatId={selectedConversation.conv_id}
                conversationName={selectedConversation.name}
                onMessageSent={() => {
                  setTimeout(() => {
                    webMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : isTwilioChannel ? (
              <TwilioChatInput
                chatId={selectedConversation.conv_id}
                onMessageSent={() => {
                  setTimeout(() => {
                    twilioMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : isEmailChannel ? (
              <EmailChatInput
                conversation={selectedConversation as EmailConversationModel}
                onMessageSent={() => {
                  setTimeout(() => {
                    emailMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : isInstagramChannel ? (
              <InstagramChatInput
                chatId={selectedConversation.conv_id}
                onMessageSent={() => {
                  setTimeout(() => {
                    instagramMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : (
              <WhatsappChatInput
                chatId={selectedConversation.conv_id}
                onMessageSent={() => {
                  setTimeout(() => {
                    messageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            )
          ) : null
        }
      >
        {isWhatsappChannel ? (
          <WhatsappChatMessageWindow
            ref={messageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : isWebChannel ? (
          <WebChatMessageWindow
            ref={webMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : isTwilioChannel ? (
          <TwilioChatMessageWindow
            ref={twilioMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : isVoiceChannel ? (
          <VoiceChatMessageWindow
            conversationId={selectedConversation.conv_id}
          />
        ) : isEmailChannel ? (
          <EmailChatMessageWindow
            ref={emailMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : (
          <InstagramChatMessageWindow
            ref={instagramMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        )}
      </MultiSectionLayout>
    </>
  );
};

export const ClientSupportChat: FC = () => {
  const { selectedConversation } = useClientSupportPageContext();

  const isInboxSinglePane = useIsInboxSinglePane();

  if (!selectedConversation) {
    // When selected conversation is missing, there is nothing to render here in the single-pane mode
    return isInboxSinglePane ? null : <SelectChatSection />;
  }

  return <ClientSupportChatInner selectedConversation={selectedConversation} />;
};
